"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import Fuse from "fuse.js";
import type { SearchItem } from "@/lib/search";

interface SearchBarProps {
  items: SearchItem[];
}

const TYPE_LABELS: Record<string, string> = {
  directory: "Directory",
  event: "Event",
  blog: "Blog",
};

export function SearchBar({ items }: SearchBarProps) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fuseRef = useRef<Fuse<SearchItem> | null>(null);

  const getFuse = useCallback(() => {
    if (!fuseRef.current) {
      fuseRef.current = new Fuse(items, {
        keys: [
          { name: "title", weight: 2 },
          { name: "description", weight: 1 },
          { name: "tags", weight: 1.5 },
          { name: "category", weight: 1 },
        ],
        threshold: 0.4,
        includeScore: true,
      });
    }
    return fuseRef.current;
  }, [items]);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    const fuse = getFuse();
    const found = fuse.search(query, { limit: 6 });
    setResults(found.map((r) => r.item));
    setIsOpen(true);
  }, [query, getFuse]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setExpanded(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setIsOpen(false);
      setExpanded(false);
      inputRef.current?.blur();
    } else if (e.key === "Enter" && query.length >= 2) {
      setIsOpen(false);
      setExpanded(false);
      router.push(`/search?q=${encodeURIComponent(query)}`);
    }
  }

  function handleResultClick(url: string) {
    setIsOpen(false);
    setExpanded(false);
    setQuery("");
    router.push(url);
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Mobile: icon toggle */}
      <button
        onClick={() => {
          setExpanded(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className={`md:hidden ${expanded ? "hidden" : ""}`}
        aria-label="Search"
      >
        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>

      {/* Search input */}
      <div className={`${expanded ? "block" : "hidden"} md:block`}>
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search..."
            className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-primary focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 md:w-56"
          />
        </div>
      </div>

      {/* Dropdown results */}
      {isOpen && results.length > 0 && (
        <div className="absolute right-0 top-full z-50 mt-1 w-80 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <ul className="max-h-96 overflow-auto py-2">
            {results.map((item) => (
              <li key={item.url}>
                <button
                  onClick={() => handleResultClick(item.url)}
                  className="flex w-full items-start gap-3 px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <span className="mt-0.5 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                    {TYPE_LABELS[item.type]}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.title}
                    </p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {item.description}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-gray-200 px-4 py-2 dark:border-gray-700">
            <button
              onClick={() => {
                setIsOpen(false);
                router.push(`/search?q=${encodeURIComponent(query)}`);
              }}
              className="text-sm text-primary hover:underline"
            >
              View all results
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
